/**
 * Command to merge two disconnected workflow branches into a single node.
 *
 * Core Logic:
 * - sourceNode is deleted, targetNode absorbs all incoming/outgoing edges
 * - All operation references to sourceNodeId are remapped to targetNodeId
 * - Quantity is recalculated as sum(source + target)
 * - targetNode retains its identity but inherits sourceNode's position
 * - creationOrder is renumbered globally across the merged workflow
 *
 * Direction Rules:
 * - ROOT clicked: sourceNodeId = clicked, targetNodeId = candidate
 * - LEAF clicked: sourceNodeId = candidate, targetNodeId = clicked
 */

import { generateId } from '@/graph/utils/id';
import {
  getConnectedComponents,
  isRootMaterial,
  isLeafMaterial,
  getMergeCandidates,
} from '@/graph/domain/topology';
import {
  calculateMergedNodeQuantity,
  renumberAfterMerge,
} from './helpers/renumberCreationOrders';
import { propagateDownstream } from './helpers/propagateDownstream';
import type {
  GraphRecords,
  MaterialNodeSpec,
  OperationNodeSpec,
  FlowEdgeSpec,
  Quantity,
} from '@/graph/domain/types';

// ─────────────────────────────────────────────────────────────────────────────

export interface MergeMaterialsPayload {
  /** Material ID to delete (but keep its position on targetNode) */
  sourceNodeId: string;
  /** Material ID to keep and absorb edges */
  targetNodeId: string;
}

export interface MergeMaterialsResult {
  /** ID of the merged node (same as targetNodeId) */
  mergedNodeId: string;
  /** IDs of edges whose source/target was rewired */
  rewiredEdgeIds: string[];
  /** ID of the deleted source node */
  deletedSourceNodeId: string;
  /** Final quantity after merge and sum */
  quantitySummed: Quantity | null;
  /** IDs of operations whose creationOrder shifted */
  shiftedOperationIds: string[];
  /** Whether mixed units were detected (volume + mass) */
  hasMixedUnits: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────

export class MergeMaterialsError extends Error {
  constructor(
    public code:
      | 'SOURCE_NOT_FOUND'
      | 'TARGET_NOT_FOUND'
      | 'SAME_MATERIAL'
      | 'SAME_COMPONENT'
      | 'INVALID_ENDPOINTS'
      | 'BOTH_GROUPED'
      | 'GROUP_MEMBER_MERGE',
    message: string,
  ) {
    super(message);
    this.name = 'MergeMaterialsError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Main merge command: combines two materials into one.
 *
 * @param graphData - The graph structure to modify
 * @param payload - source/target material IDs
 * @param contextType - 'ROOT' or 'LEAF' (which endpoint type was clicked)
 * @returns Result with merged node ID, rewired edges, and affected operations
 */
export function mergeMaterials(
  graphData: GraphRecords,
  payload: MergeMaterialsPayload,
  contextType: 'ROOT' | 'LEAF',
): MergeMaterialsResult {
  const { sourceNodeId, targetNodeId } = payload;

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 1: VALIDATION
  // ─────────────────────────────────────────────────────────────────────────

  if (!graphData.materialNodes[sourceNodeId]) {
    throw new MergeMaterialsError(
      'SOURCE_NOT_FOUND',
      `Source material ${sourceNodeId} not found`,
    );
  }
  if (!graphData.materialNodes[targetNodeId]) {
    throw new MergeMaterialsError(
      'TARGET_NOT_FOUND',
      `Target material ${targetNodeId} not found`,
    );
  }
  if (sourceNodeId === targetNodeId) {
    throw new MergeMaterialsError(
      'SAME_MATERIAL',
      'Cannot merge a material with itself',
    );
  }

  // Determine endpoint types
  const sourceIsRoot = isRootMaterial(sourceNodeId, graphData.flowEdges);
  const sourceIsLeaf = isLeafMaterial(sourceNodeId, graphData.flowEdges);
  const targetIsRoot = isRootMaterial(targetNodeId, graphData.flowEdges);
  const targetIsLeaf = isLeafMaterial(targetNodeId, graphData.flowEdges);

  // Validate endpoints per contextType
  if (contextType === 'ROOT') {
    // sourceNode must be root, targetNode must NOT be root (opposite endpoint)
    if (!sourceIsRoot) {
      throw new MergeMaterialsError(
        'INVALID_ENDPOINTS',
        'Clicked material must be a root (source material with no incoming edges)',
      );
    }
    if (targetIsRoot) {
      throw new MergeMaterialsError(
        'INVALID_ENDPOINTS',
        'Candidate material must not be a root (both cannot be roots)',
      );
    }
  } else if (contextType === 'LEAF') {
    // sourceNode must be leaf, targetNode must NOT be leaf
    if (!sourceIsLeaf) {
      throw new MergeMaterialsError(
        'INVALID_ENDPOINTS',
        'Clicked material must be a leaf (destination material with no outgoing edges)',
      );
    }
    if (targetIsLeaf) {
      throw new MergeMaterialsError(
        'INVALID_ENDPOINTS',
        'Candidate material must not be a leaf (both cannot be leaves)',
      );
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // CRITICAL FIX: Always delete ROOT, always keep LEAF
  // This matches the old merge behavior where root nodes are absorbed into leaf nodes.
  // If targetNode is ROOT, we need to swap source and target so source = ROOT (deleted)
  // and target = LEAF (kept).
  // ──────────────────────────────────────────────────────────────────────────
  
  let nodeToDelete = sourceNodeId;
  let nodeToKeep = targetNodeId;
  let isSourceRoot = sourceIsRoot;
  let isTargetRoot = targetIsRoot;

  if (targetIsRoot && !sourceIsRoot) {
    // Target is ROOT, Source is LEAF → swap to ensure ROOT is deleted
    nodeToDelete = targetNodeId;
    nodeToKeep = sourceNodeId;
    isSourceRoot = targetIsRoot;
    isTargetRoot = sourceIsRoot;
  }

  // Update payload variables to use the correct deletion/keeping logic
  const sourceNodeIdToDelete = nodeToDelete;
  const targetNodeIdToKeep = nodeToKeep;

  // Check different connected components (must traverse graphsLink edges)
  const components = getConnectedComponents(graphData);
  const sourceComponent = components.find((comp) => comp.materialIds.has(sourceNodeId));
  const targetComponent = components.find((comp) => comp.materialIds.has(targetNodeId));
  if (sourceComponent === targetComponent) {
    throw new MergeMaterialsError(
      'SAME_COMPONENT',
      'Materials must be in different connected branches (traverse graphsLink edges)',
    );
  }

  // Check not group members (TBD: for now block)
  if (graphData.groups) {
    for (const group of Object.values(graphData.groups)) {
      if (group.stepIds?.includes(sourceNodeId)) {
        throw new MergeMaterialsError(
          'GROUP_MEMBER_MERGE',
          'Cannot merge: source material is a member of a group',
        );
      }
      if (group.stepIds?.includes(targetNodeId)) {
        throw new MergeMaterialsError(
          'GROUP_MEMBER_MERGE',
          'Cannot merge: target material is a member of a group',
        );
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 2: REWIRE FLOW EDGES
  // ─────────────────────────────────────────────────────────────────────────

  const rewiredEdgeIds: string[] = [];
  const edgesWithSource = new Set<string>();

  // Find all edges pointing to/from nodeToDelete and rewire them to nodeToKeep
  for (const edgeId of Object.keys(graphData.flowEdges)) {
    const edge = graphData.flowEdges[edgeId];

    if (edge.sourceId === sourceNodeIdToDelete) {
      // Redirect outgoing edge to come from nodeToKeep
      edge.sourceId = targetNodeIdToKeep;
      rewiredEdgeIds.push(edgeId);
      edgesWithSource.add(edgeId);
    } else if (edge.targetId === sourceNodeIdToDelete) {
      // Redirect incoming edge to go to nodeToKeep
      edge.targetId = targetNodeIdToKeep;
      rewiredEdgeIds.push(edgeId);
    }
  }

  // Drop duplicate edges (two edges with same source→target to targetNode)
  const seen = new Set<string>();
  const edgesToDelete: string[] = [];
  for (const edgeId of Object.keys(graphData.flowEdges)) {
    const edge = graphData.flowEdges[edgeId];
    const key = `${edge.sourceId}→${edge.targetId}`;

    if (seen.has(key)) {
      edgesToDelete.push(edgeId);
    }
    seen.add(key);
  }

  for (const edgeId of edgesToDelete) {
    delete graphData.flowEdges[edgeId];
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 3: DEEP REMAP ID REFERENCES IN OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────

  for (const opId of Object.keys(graphData.operationNodes)) {
    const op = graphData.operationNodes[opId];

    // Transfer operation fields
    if (op.transferRow) {
      if (op.transferRow.sourceMaterialId === sourceNodeIdToDelete) {
        op.transferRow.sourceMaterialId = targetNodeIdToKeep;
      }
      if (op.transferRow.destinationMaterialId === sourceNodeIdToDelete) {
        op.transferRow.destinationMaterialId = targetNodeIdToKeep;
      }
      if (op.transferRow.sourcePostMaterialId === sourceNodeIdToDelete) {
        op.transferRow.sourcePostMaterialId = targetNodeIdToKeep;
      }
      if (op.transferRow.destinationPostMaterialId === sourceNodeIdToDelete) {
        op.transferRow.destinationPostMaterialId = targetNodeIdToKeep;
      }
    }
  }

  // Track which materials had their sourceMaterials rewired (for STEP 3.5)
  const materialsWithRewiredSources = new Set<string>();

  // Remap sourceNodeIdToDelete → targetNodeIdToKeep in all materials' metadata
  for (const material of Object.values(graphData.materialNodes)) {
    // Update sourceMaterials array (list of source material objects)
    if (material.sourceMaterials && Array.isArray(material.sourceMaterials)) {
      for (const sourceMat of material.sourceMaterials) {
        if (sourceMat.sourceVertexId === sourceNodeIdToDelete) {
          sourceMat.sourceVertexId = targetNodeIdToKeep;
          // Track this material for name update in STEP 3.5
          if (material.id) {
            materialsWithRewiredSources.add(material.id);
          }
        }
      }
    }

    // Update composition.directContributions
    if (material.composition?.directContributions) {
      for (const contrib of material.composition.directContributions) {
        if (contrib.fromNodeId === sourceNodeIdToDelete) {
          contrib.fromNodeId = targetNodeIdToKeep;
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 3.5: UPDATE DEPENDENT MATERIAL NAMES (CASCADING)
  // ─────────────────────────────────────────────────────────────────────────
  // Only rename materials whose name matches the deleted material's name.
  // Then cascade: find materials whose source points to these materials, and update 
  // their sourceMaterials metadata. Only rename those if they also match deleted name.
  // Example: m1→m2→m3 where m2.name==m1.name. When m1 is deleted:
  //   - m2 gets renamed (name matches deleted m1)
  //   - m2's sourceMaterials[].name updated to new target
  //   - m3 cascades if m3.name==m1.name, else only its sourceMaterials[] updated

  const sourceNode = graphData.materialNodes[sourceNodeIdToDelete];
  const sourceNodeName = sourceNode?.name ?? 'unknown';

  const targetNodeData = graphData.materialNodes[targetNodeIdToKeep];
  const targetNodeName = targetNodeData?.name ?? 'unknown';
  const targetNodeContainerName = targetNodeData?.container?.name ?? targetNodeName;

  // BFS: Track materials to process, starting with those that had rewired sources
  const materialsToProcess = new Set(materialsWithRewiredSources);
  const materialsProcessed = new Set<string>();

  while (materialsToProcess.size > 0) {
    const materialId = materialsToProcess.values().next().value as string;
    materialsToProcess.delete(materialId);

    if (materialsProcessed.has(materialId)) continue;
    materialsProcessed.add(materialId);

    const material = graphData.materialNodes[materialId];
    if (!material) continue;

    // Only rename this material if its name matches the deleted node's name
    if (material.name === sourceNodeName) {
      material.name = targetNodeName;
      if (material.container) {
        material.container.name = targetNodeContainerName;
      }
    }

    // Update sourceMaterials entries (always update these)
    if (material.sourceMaterials && Array.isArray(material.sourceMaterials)) {
      for (const sourceMat of material.sourceMaterials) {
        if (sourceMat.sourceVertexId === targetNodeIdToKeep) {
          sourceMat.name = targetNodeName;
          if (sourceMat.container) {
            sourceMat.container.name = targetNodeContainerName;
          }
        }
      }
    }

    // Find all materials whose sourceMaterials point to THIS material's vertexId
    // These are downstream materials in the chain
    const thisVertexId = material.vertexId ?? material.id;
    for (const potentialDownstream of Object.values(graphData.materialNodes)) {
      if (!potentialDownstream.id || potentialDownstream.id === targetNodeIdToKeep) continue;
      if (materialsProcessed.has(potentialDownstream.id)) continue;

      if (potentialDownstream.sourceMaterials && Array.isArray(potentialDownstream.sourceMaterials)) {
        for (const sourceMat of potentialDownstream.sourceMaterials) {
          if (sourceMat.sourceVertexId === thisVertexId) {
            materialsToProcess.add(potentialDownstream.id);
            break;
          }
        }
      }
    }
  }

  // NOTE: Renumbering is deferred to STEP 6.5 (after source deletion)
  // This ensures the topological sort works on the final graph state

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 4: QUANTITY COMPUTATION
  // ─────────────────────────────────────────────────────────────────────────

  const { summedQuantity, hasMixedUnits } = calculateMergedNodeQuantity(
    sourceNodeIdToDelete,
    targetNodeIdToKeep,
    graphData,
  );

  if (summedQuantity) {
    graphData.materialNodes[targetNodeIdToKeep].quantity = summedQuantity;
  } else if (hasMixedUnits) {
    // Log warning but don't block merge
    console.warn(
      `[MERGE] Mixed unit types detected (volume+mass) on merged node ${targetNodeIdToKeep}. Quantity set to null.`,
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 5: POSITION TRANSFER
  // ─────────────────────────────────────────────────────────────────────────

  const targetNode = graphData.materialNodes[targetNodeIdToKeep];

  if (sourceNode?.position && targetNode) {
    targetNode.position = sourceNode.position;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 6: DELETE ROOT NODE (nodeToDelete)
  // ─────────────────────────────────────────────────────────────────────────

  // CAPTURE source quantity BEFORE deletion (needed for COMPLEX operation recalculation)
  const sourceQuantityBeforeMerge = sourceNode?.quantity;
  // ─────────────────────────────────────────────────────────────────────────
  // STEP 6.1: CALCULATE REMOVED CONTRIBUTIONS BEFORE UPDATING ioMappings
  // ─────────────────────────────────────────────────────────────────────────
  // For COMPLEX operations, calculate what contribution the deleted input had to each output
  // We must do this BEFORE we update ioMappings, otherwise we won't find the mappings!
  
  const removedContributionsByOutput = new Map<string, number>(); // outputMatId -> total removed contribution

  for (const operationId in graphData.operationNodes) {
    const operation = graphData.operationNodes[operationId];
    if (operation.archetype !== 'COMPLEX') continue;

    const ioMappings = operation.complexOptions?.ioMapping || [];
    
    // Find all mappings from the deleted source material
    for (const mapping of ioMappings) {
      if (mapping.inputMaterialId === sourceNodeIdToDelete) {
        const fraction = mapping.fraction || 0;
        const outputMatId = mapping.outputMaterialId;
        const removedContribution = (sourceQuantityBeforeMerge?.value?.amount || 0) * fraction;
        
        // Accumulate in case there are multiple mappings to the same output
        const currentRemoved = removedContributionsByOutput.get(outputMatId) || 0;
        removedContributionsByOutput.set(outputMatId, currentRemoved + removedContribution);
      }
    }
  }

  delete graphData.materialNodes[sourceNodeIdToDelete];

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 6.2: UPDATE ioMappings IN COMPLEX OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────
  // When we merge materials, any COMPLEX operations that had ioMappings
  // referencing the DELETED source material must now reference the KEPT target material.
  //
  // Example: If m1 → COMPLEX had inputMaterialId=m1 and we delete m1, update it to use m5 instead.

  for (const operationId in graphData.operationNodes) {
    const operation = graphData.operationNodes[operationId];
    if (operation.archetype !== 'COMPLEX') continue;

    const ioMappings = operation.complexOptions?.ioMapping || [];
    for (const mapping of ioMappings) {
      // If this mapping references the deleted source material, update it to reference the kept target
      if (mapping.inputMaterialId === sourceNodeIdToDelete) {
        mapping.inputMaterialId = targetNodeIdToKeep;
      }
      if (mapping.outputMaterialId === sourceNodeIdToDelete) {
        mapping.outputMaterialId = targetNodeIdToKeep;
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 6.5: REORDER OPERATIONS FOR TRANSLATION PANEL (after deletion)
  // ─────────────────────────────────────────────────────────────────────────
  // Now that the source material is deleted, renumber all operation creationOrders
  // to reflect the new material dependency chain. This ensures the Translation 
  // panel displays steps in the correct merged order.
  //
  // Pattern: [before merge] → [leaf chain] → [root chain] → [after merge]

  renumberAfterMerge(graphData, targetNodeIdToKeep, sourceNodeIdToDelete);

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 7: PROPAGATE QUANTITIES DOWNSTREAM FROM MERGED NODE
  // ─────────────────────────────────────────────────────────────────────────
  // Pass merge context so COMPLEX operations can properly recalculate
  // (knowing which input was deleted and its contributions to each output)

  propagateDownstream(graphData, targetNodeIdToKeep, {
    mergeContext: {
      deletedInputMaterialId: sourceNodeIdToDelete,
      deletedInputQuantity: sourceQuantityBeforeMerge?.value?.amount || 0,
      removedContributionsByOutput,
    },
  });

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 8: DETECT SHIFTED OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────
  // Note: normalizeCreationOrders is NOT called here because renumberAfterMerge
  // (STEP 6.5) already renumbered all operations in the correct topological order.
  // Calling normalizeCreationOrders here would undo that work.

  // For now, all operations are considered "shifted" since merge reordered them
  const shiftedOperationIds: string[] = Object.keys(graphData.operationNodes);

  // ─────────────────────────────────────────────────────────────────────────
  // RETURN RESULT
  // ─────────────────────────────────────────────────────────────────────────

  return {
    mergedNodeId: targetNodeIdToKeep,
    rewiredEdgeIds,
    deletedSourceNodeId: sourceNodeIdToDelete,
    quantitySummed: summedQuantity || null,
    shiftedOperationIds,
    hasMixedUnits,
  };
}

